import { useReadItems } from './useReadItems'
import { useViewedItems } from './useViewedItems'
import type { SiteSettings } from '../store/feature-flags/useSiteSettingsStore'

type ItemWithId = {
  id: string
  publishedDate?: string
  clusterSize?: number | null
}
type DerankMode = SiteSettings['derankItemsByStatus']

/**
 * Composable for managing feed deranking logic
 *
 * Provides functionality to:
 * - Bucket/derank items by status (viewed, read, both, or none)
 * - Preserve original sort order within each bucket
 *
 * @returns Object containing deranking methods
 *
 */
export function useFeedDeranking() {
  const { isItemRead } = useReadItems()
  const { isItemPreviouslyViewed } = useViewedItems()

  /**
   * Sorts items by status and applies secondary sorting within buckets
   * - none: No deranking, but applies global freshness sort (predictable)
   * - viewed: Items not viewed first, then viewed items
   * - read: Items not read first, then read items
   * - both: Items not viewed/read first, then viewed/read items
   *
   * @param items - Array of items to sort
   * @param mode - Deranking mode to apply
   * @returns New sorted array (original array is not modified)
   */
  function sortItemsByStatus<T extends ItemWithId>(
    items: T[],
    mode: DerankMode
  ): T[] {
    // Keep API deterministic: even when no deranking, apply freshness sort
    if (mode === 'none') return items

    const getDerankScore = (it: T): number => {
      const viewed = isItemPreviouslyViewed(it.id) ? 1 : 0
      const read = isItemRead(it.id) ? 1 : 0
      switch (mode) {
        case 'viewed':
          return viewed
        case 'read':
          return read
        case 'both':
          return Math.max(viewed, read)
        default:
          return 0
      }
    }

    const withScore = items.map((it) => ({ it, score: getDerankScore(it) }))
    const top = withScore.filter(({ score }) => score === 0).map(({ it }) => it)
    const bottom = withScore
      .filter(({ score }) => score > 0)
      .map(({ it }) => it)

    return [...top, ...bottom]
  }

  return { sortItemsByStatus }
}
